/**
 * Canonical map of wheel-variant URL slugs per language.
 * Single source of truth for the router (App.js) and any build script that
 * needs to enumerate real URLs (generate-sitemap.js, prerender.js).
 *
 * CommonJS on purpose: plain Node build scripts `require()` this file
 * directly (no babel step), and named imports from a CommonJS export work
 * fine from webpack/babel-bundled React source too.
 */

const LANGUAGES = [
  'en', 'es', 'de', 'ja', 'fr', 'pt-BR', 'ru', 'it', 'zh-CN', 'nl',
  'pl', 'tr', 'ko', 'ar', 'hi', 'vi', 'th', 'sv', 'cs', 'el', 'id'
];

// All wheel variant keys (excludes 'main', the homepage/no-variant case)
const VARIANTS = [
  'yesNo', 'namePicker', 'teamGenerator', 'numberWheel', 'letterWheel',
  'colorWheel', 'foodWheel', 'moviePicker', 'gamePicker', 'giveawayWheel',
  'truthOrDare', 'questionWheel', 'casinoWheel', 'datePicker', 'countryWheel',
  'drinkingGame', 'musicWheel', 'sportsWheel', 'activityWheel'
];

const VARIANT_SLUGS = {
  en: {
    main: '',
    yesNo: 'yes-no-wheel',
    namePicker: 'name-picker',
    teamGenerator: 'team-generator',
    numberWheel: 'number-wheel',
    letterWheel: 'letter-wheel',
    colorWheel: 'color-wheel',
    foodWheel: 'food-wheel',
    moviePicker: 'movie-picker',
    gamePicker: 'game-picker',
    giveawayWheel: 'giveaway-wheel',
    truthOrDare: 'truth-or-dare',
    questionWheel: 'question-wheel',
    casinoWheel: 'casino-wheel',
    datePicker: 'date-picker',
    countryWheel: 'country-wheel',
    drinkingGame: 'drinking-game',
    musicWheel: 'music-wheel',
    sportsWheel: 'sports-wheel',
    activityWheel: 'activity-wheel'
  },
  it: {
    main: '',
    yesNo: 'ruota-si-no',
    namePicker: 'sorteggio-nomi',
    teamGenerator: 'generatore-squadre',
    numberWheel: 'ruota-numeri',
    letterWheel: 'ruota-lettere',
    colorWheel: 'ruota-colori',
    foodWheel: 'ruota-cibo',
    moviePicker: 'selettore-film',
    gamePicker: 'selettore-giochi',
    giveawayWheel: 'ruota-giveaway',
    truthOrDare: 'obbligo-verita',
    questionWheel: 'ruota-domande',
    casinoWheel: 'ruota-casino',
    datePicker: 'ruota-date',
    countryWheel: 'ruota-paesi',
    drinkingGame: 'gioco-alcolico',
    musicWheel: 'ruota-musica',
    sportsWheel: 'ruota-sport',
    activityWheel: 'ruota-attivita'
  },
  es: {
    main: '',
    yesNo: 'ruleta-si-no',
    namePicker: 'selector-nombres',
    teamGenerator: 'generador-equipos',
    numberWheel: 'ruleta-numeros',
    letterWheel: 'ruleta-letras',
    colorWheel: 'ruleta-colores',
    foodWheel: 'ruleta-comida',
    moviePicker: 'selector-peliculas',
    gamePicker: 'selector-juegos',
    giveawayWheel: 'ruleta-sorteo',
    truthOrDare: 'verdad-reto',
    questionWheel: 'ruleta-preguntas',
    casinoWheel: 'ruleta-casino',
    datePicker: 'ruleta-fechas',
    countryWheel: 'ruleta-paises',
    drinkingGame: 'juego-beber',
    musicWheel: 'ruleta-musica',
    sportsWheel: 'ruleta-deportes',
    activityWheel: 'ruleta-actividades'
  },
  de: {
    main: '',
    yesNo: 'ja-nein-rad',
    namePicker: 'namenspicker',
    teamGenerator: 'team-generator',
    numberWheel: 'zahlenrad',
    letterWheel: 'buchstabenrad',
    colorWheel: 'farbrad',
    foodWheel: 'essensrad',
    moviePicker: 'filmauswaehler',
    gamePicker: 'spielauswaehler',
    giveawayWheel: 'gewinnspiel-rad',
    truthOrDare: 'wahrheit-pflicht',
    questionWheel: 'fragenrad',
    casinoWheel: 'casino-rad',
    datePicker: 'datumsrad',
    countryWheel: 'laenderrad',
    drinkingGame: 'trinkspiel',
    musicWheel: 'musikrad',
    sportsWheel: 'sportrad',
    activityWheel: 'aktivitaetenrad'
  },
  fr: {
    main: '',
    yesNo: 'roue-oui-non',
    namePicker: 'selecteur-noms',
    teamGenerator: 'generateur-equipes',
    numberWheel: 'roue-nombres',
    letterWheel: 'roue-lettres',
    colorWheel: 'roue-couleurs',
    foodWheel: 'roue-nourriture',
    moviePicker: 'selecteur-films',
    gamePicker: 'selecteur-jeux',
    giveawayWheel: 'roue-tirage',
    truthOrDare: 'action-verite',
    questionWheel: 'roue-questions',
    casinoWheel: 'roue-casino',
    datePicker: 'roue-dates',
    countryWheel: 'roue-pays',
    drinkingGame: 'jeu-boire',
    musicWheel: 'roue-musique',
    sportsWheel: 'roue-sports',
    activityWheel: 'roue-activites'
  },
  ja: {
    main: '',
    yesNo: 'yes-no-hoiru',
    namePicker: 'namae-erabi',
    teamGenerator: 'chiimu-sakusei',
    numberWheel: 'suuji-hoiru',
    letterWheel: 'moji-hoiru',
    colorWheel: 'iro-hoiru',
    foodWheel: 'tabemono-hoiru',
    moviePicker: 'eiga-erabi',
    gamePicker: 'geemu-erabi',
    giveawayWheel: 'purezen-hoiru',
    truthOrDare: 'shinjitsu-chousen',
    questionWheel: 'shitsumon-hoiru',
    casinoWheel: 'kajino-hoiru',
    datePicker: 'hidzuke-erabi',
    countryWheel: 'kuni-hoiru',
    drinkingGame: 'nomi-geemu',
    musicWheel: 'ongaku-hoiru',
    sportsWheel: 'supootsu-hoiru',
    activityWheel: 'katsudou-hoiru'
  },
  'pt-BR': {
    main: '',
    yesNo: 'roleta-sim-nao',
    namePicker: 'sorteador-nomes',
    teamGenerator: 'gerador-equipes',
    numberWheel: 'roleta-numeros',
    letterWheel: 'roleta-letras',
    colorWheel: 'roleta-cores',
    foodWheel: 'roleta-comida',
    moviePicker: 'escolher-filme',
    gamePicker: 'escolher-jogo',
    giveawayWheel: 'roleta-sorteio',
    truthOrDare: 'verdade-desafio',
    questionWheel: 'roleta-perguntas',
    casinoWheel: 'roleta-cassino',
    datePicker: 'roleta-datas',
    countryWheel: 'roleta-paises',
    drinkingGame: 'jogo-bebida',
    musicWheel: 'roleta-musica',
    sportsWheel: 'roleta-esportes',
    activityWheel: 'roleta-atividades'
  },
  ru: {
    main: '',
    yesNo: 'koleso-da-net',
    namePicker: 'vyibor-imeni',
    teamGenerator: 'generator-komand',
    numberWheel: 'koleso-chisel',
    letterWheel: 'koleso-bukv',
    colorWheel: 'koleso-tsvetov',
    foodWheel: 'koleso-edy',
    moviePicker: 'vyibor-filma',
    gamePicker: 'vyibor-igry',
    giveawayWheel: 'koleso-rozygrysha',
    truthOrDare: 'pravda-ili-deystvie',
    questionWheel: 'koleso-voprosov',
    casinoWheel: 'koleso-kazino',
    datePicker: 'koleso-dat',
    countryWheel: 'koleso-stran',
    drinkingGame: 'alkogolnaya-igra',
    musicWheel: 'koleso-muzyki',
    sportsWheel: 'koleso-sporta',
    activityWheel: 'koleso-aktivnostey'
  },
  'zh-CN': {
    main: '',
    yesNo: 'shifou-zhuanpan',
    namePicker: 'mingzi-choujiang',
    teamGenerator: 'tuandui-shengcheng',
    numberWheel: 'shuzi-zhuanpan',
    letterWheel: 'zimu-zhuanpan',
    colorWheel: 'yanse-zhuanpan',
    foodWheel: 'shiwu-zhuanpan',
    moviePicker: 'dianying-xuanze',
    gamePicker: 'youxi-xuanze',
    giveawayWheel: 'choujiang-zhuanpan',
    truthOrDare: 'zhenxin-damaoxian',
    questionWheel: 'wenti-zhuanpan',
    casinoWheel: 'duchang-zhuanpan',
    datePicker: 'riqi-zhuanpan',
    countryWheel: 'guojia-zhuanpan',
    drinkingGame: 'hejiu-youxi',
    musicWheel: 'yinyue-zhuanpan',
    sportsWheel: 'tiyu-zhuanpan',
    activityWheel: 'huodong-zhuanpan'
  },
  nl: {
    main: '',
    yesNo: 'ja-nee-wiel',
    namePicker: 'naam-picker',
    teamGenerator: 'team-generator',
    numberWheel: 'nummer-wiel',
    letterWheel: 'letter-wiel',
    colorWheel: 'kleur-wiel',
    foodWheel: 'eten-wiel',
    moviePicker: 'film-kiezer',
    gamePicker: 'spel-kiezer',
    giveawayWheel: 'weggeef-wiel',
    truthOrDare: 'waarheid-durven',
    questionWheel: 'vraag-wiel',
    casinoWheel: 'casino-wiel',
    datePicker: 'datum-wiel',
    countryWheel: 'land-wiel',
    drinkingGame: 'drink-spel',
    musicWheel: 'muziek-wiel',
    sportsWheel: 'sport-wiel',
    activityWheel: 'activiteit-wiel'
  },
  pl: {
    main: '',
    yesNo: 'kolo-tak-nie',
    namePicker: 'losowanie-imion',
    teamGenerator: 'generator-druzyn',
    numberWheel: 'kolo-liczb',
    letterWheel: 'kolo-liter',
    colorWheel: 'kolo-kolorow',
    foodWheel: 'kolo-jedzenia',
    moviePicker: 'wybor-filmu',
    gamePicker: 'wybor-gry',
    giveawayWheel: 'kolo-losowania',
    truthOrDare: 'prawda-wyzwanie',
    questionWheel: 'kolo-pytan',
    casinoWheel: 'kolo-kasyno',
    datePicker: 'kolo-dat',
    countryWheel: 'kolo-krajow',
    drinkingGame: 'gra-alkoholowa',
    musicWheel: 'kolo-muzyki',
    sportsWheel: 'kolo-sportu',
    activityWheel: 'kolo-aktywnosci'
  },
  tr: {
    main: '',
    yesNo: 'evet-hayir-carki',
    namePicker: 'isim-secici',
    teamGenerator: 'takim-olusturucu',
    numberWheel: 'sayi-carki',
    letterWheel: 'harf-carki',
    colorWheel: 'renk-carki',
    foodWheel: 'yemek-carki',
    moviePicker: 'film-secici',
    gamePicker: 'oyun-secici',
    giveawayWheel: 'cekilis-carki',
    truthOrDare: 'dogru-cesaret',
    questionWheel: 'soru-carki',
    casinoWheel: 'casino-carki',
    datePicker: 'tarih-carki',
    countryWheel: 'ulke-carki',
    drinkingGame: 'icki-oyunu',
    musicWheel: 'muzik-carki',
    sportsWheel: 'spor-carki',
    activityWheel: 'aktivite-carki'
  },
  ko: {
    main: '',
    yesNo: 'yesno-hwil',
    namePicker: 'ileum-ppobgi',
    teamGenerator: 'tim-saengseonggi',
    numberWheel: 'sunja-hwil',
    letterWheel: 'munja-hwil',
    colorWheel: 'saek-hwil',
    foodWheel: 'eumsik-hwil',
    moviePicker: 'yeonghwa-seontaek',
    gamePicker: 'geim-seontaek',
    giveawayWheel: 'gyeongpum-hwil',
    truthOrDare: 'jinsil-yonggi',
    questionWheel: 'jilmun-hwil',
    casinoWheel: 'casino-hwil',
    datePicker: 'naljja-hwil',
    countryWheel: 'gukga-hwil',
    drinkingGame: 'sul-geim',
    musicWheel: 'eumak-hwil',
    sportsWheel: 'seupocheu-hwil',
    activityWheel: 'hwaldong-hwil'
  },
  ar: {
    main: '',
    yesNo: 'ajala-naam-la',
    namePicker: 'mukhtaar-alasmaa',
    teamGenerator: 'munshee-alfiraq',
    numberWheel: 'ajala-alarqam',
    letterWheel: 'ajala-alhuruf',
    colorWheel: 'ajala-alalwan',
    foodWheel: 'ajala-altaam',
    moviePicker: 'mukhtaar-alfilm',
    gamePicker: 'mukhtaar-allaeba',
    giveawayWheel: 'ajala-alsuhub',
    truthOrDare: 'haqiqa-aw-tahadi',
    questionWheel: 'ajala-alasila',
    casinoWheel: 'ajala-alkasino',
    datePicker: 'ajala-altarikh',
    countryWheel: 'ajala-aldual',
    drinkingGame: 'luebat-shurb',
    musicWheel: 'ajala-almusiqa',
    sportsWheel: 'ajala-alriyadha',
    activityWheel: 'ajala-alanshita'
  },
  hi: {
    main: '',
    yesNo: 'haan-nahi-chakra',
    namePicker: 'naam-chunav',
    teamGenerator: 'team-nirmata',
    numberWheel: 'sankhya-chakra',
    letterWheel: 'akshar-chakra',
    colorWheel: 'rang-chakra',
    foodWheel: 'khana-chakra',
    moviePicker: 'film-chunav',
    gamePicker: 'khel-chunav',
    giveawayWheel: 'uphaar-chakra',
    truthOrDare: 'sach-himmat',
    questionWheel: 'prashn-chakra',
    casinoWheel: 'casino-chakra',
    datePicker: 'tarikh-chakra',
    countryWheel: 'desh-chakra',
    drinkingGame: 'sharab-khel',
    musicWheel: 'sangeet-chakra',
    sportsWheel: 'khel-chakra',
    activityWheel: 'gatividhi-chakra'
  },
  vi: {
    main: '',
    yesNo: 'vong-quay-co-khong',
    namePicker: 'chon-ten',
    teamGenerator: 'tao-doi',
    numberWheel: 'vong-quay-so',
    letterWheel: 'vong-quay-chu',
    colorWheel: 'vong-quay-mau',
    foodWheel: 'vong-quay-mon-an',
    moviePicker: 'chon-phim',
    gamePicker: 'chon-tro-choi',
    giveawayWheel: 'vong-quay-qua-tang',
    truthOrDare: 'that-hay-tham',
    questionWheel: 'vong-quay-cau-hoi',
    casinoWheel: 'vong-quay-casino',
    datePicker: 'vong-quay-ngay',
    countryWheel: 'vong-quay-quoc-gia',
    drinkingGame: 'tro-uong',
    musicWheel: 'vong-quay-nhac',
    sportsWheel: 'vong-quay-the-thao',
    activityWheel: 'vong-quay-hoat-dong'
  },
  th: {
    main: '',
    yesNo: 'wong-chai-mai',
    namePicker: 'suem-chue',
    teamGenerator: 'saang-tim',
    numberWheel: 'wong-tualeek',
    letterWheel: 'wong-tuaakson',
    colorWheel: 'wong-see',
    foodWheel: 'wong-aahaan',
    moviePicker: 'lueak-nang',
    gamePicker: 'lueak-game',
    giveawayWheel: 'wong-lucky',
    truthOrDare: 'jing-hai-tham',
    questionWheel: 'wong-khamtham',
    casinoWheel: 'wong-casino',
    datePicker: 'wong-wantee',
    countryWheel: 'wong-prathet',
    drinkingGame: 'game-deum',
    musicWheel: 'wong-pleng',
    sportsWheel: 'wong-geela',
    activityWheel: 'wong-gijakam'
  },
  sv: {
    main: '',
    yesNo: 'ja-nej-hjul',
    namePicker: 'namn-vaeljare',
    teamGenerator: 'lag-generator',
    numberWheel: 'nummer-hjul',
    letterWheel: 'bokstav-hjul',
    colorWheel: 'faerg-hjul',
    foodWheel: 'mat-hjul',
    moviePicker: 'film-vaeljare',
    gamePicker: 'spel-vaeljare',
    giveawayWheel: 'utlottning-hjul',
    truthOrDare: 'sanning-konsekvens',
    questionWheel: 'fraga-hjul',
    casinoWheel: 'kasino-hjul',
    datePicker: 'datum-hjul',
    countryWheel: 'land-hjul',
    drinkingGame: 'dryck-spel',
    musicWheel: 'musik-hjul',
    sportsWheel: 'sport-hjul',
    activityWheel: 'aktivitet-hjul'
  },
  cs: {
    main: '',
    yesNo: 'kolo-ano-ne',
    namePicker: 'vyber-jmen',
    teamGenerator: 'generator-tymu',
    numberWheel: 'kolo-cisel',
    letterWheel: 'kolo-pismen',
    colorWheel: 'kolo-barev',
    foodWheel: 'kolo-jidla',
    moviePicker: 'vyber-filmu',
    gamePicker: 'vyber-hry',
    giveawayWheel: 'kolo-souteze',
    truthOrDare: 'pravda-odvaha',
    questionWheel: 'kolo-otazek',
    casinoWheel: 'kolo-kasino',
    datePicker: 'kolo-dat',
    countryWheel: 'kolo-zemi',
    drinkingGame: 'piti-hra',
    musicWheel: 'kolo-hudby',
    sportsWheel: 'kolo-sportu',
    activityWheel: 'kolo-aktivit'
  },
  el: {
    main: '',
    yesNo: 'nai-ochi-trochos',
    namePicker: 'epilogi-onomatos',
    teamGenerator: 'dimiourgia-omadas',
    numberWheel: 'trochos-arithmon',
    letterWheel: 'trochos-grammaton',
    colorWheel: 'trochos-chromaton',
    foodWheel: 'trochos-fagiton',
    moviePicker: 'epilogi-tainias',
    gamePicker: 'epilogi-paixnidiou',
    giveawayWheel: 'trochos-dorean',
    truthOrDare: 'alitheia-tolmi',
    questionWheel: 'trochos-erotiseon',
    casinoWheel: 'trochos-kazino',
    datePicker: 'trochos-imerominias',
    countryWheel: 'trochos-choron',
    drinkingGame: 'poto-paixnidi',
    musicWheel: 'trochos-mousikis',
    sportsWheel: 'trochos-athlimaton',
    activityWheel: 'trochos-drastiriotiton'
  },
  id: {
    main: '',
    yesNo: 'roda-ya-tidak',
    namePicker: 'pemilih-nama',
    teamGenerator: 'pembuat-tim',
    numberWheel: 'roda-angka',
    letterWheel: 'roda-huruf',
    colorWheel: 'roda-warna',
    foodWheel: 'roda-makanan',
    moviePicker: 'pemilih-film',
    gamePicker: 'pemilih-game',
    giveawayWheel: 'roda-giveaway',
    truthOrDare: 'jujur-tantangan',
    questionWheel: 'roda-pertanyaan',
    casinoWheel: 'roda-kasino',
    datePicker: 'roda-tanggal',
    countryWheel: 'roda-negara',
    drinkingGame: 'permainan-minum',
    musicWheel: 'roda-musik',
    sportsWheel: 'roda-olahraga',
    activityWheel: 'roda-aktivitas'
  }
};

module.exports = { LANGUAGES, VARIANTS, VARIANT_SLUGS };
